import { estadoDelPrestamo } from './prestamo';

// El recibo del abono, para imprimir o guardar como PDF.
//
// Es lo que el cliente pide en la mano cuando entrega la plata, y hasta ahora
// el programa no daba ninguno: quien cobraba anotaba en un cuaderno y el
// cliente se iba sin papel.
//
// No usa ninguna librería. Se abre una ventana con el recibo y se manda a
// imprimir: desde ahí el navegador ofrece «Guardar como PDF», y en el teléfono
// se puede compartir por WhatsApp. Meter un generador de PDF habría añadido
// un cuarto de mega y una dependencia más que vigilar, para hacer lo mismo.

const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete',
                  'ocho', 'nueve', 'diez', 'once', 'doce', 'trece', 'catorce',
                  'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve',
                  'veinte', 'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro',
                  'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho',
                  'veintinueve'];
const DECENAS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta',
                 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos',
                  'quinientos', 'seiscientos', 'setecientos', 'ochocientos',
                  'novecientos'];

// Hasta 999. Es la pieza con la que se arman los miles y los millones.
function hasta999(n) {
  if (n === 0) return '';
  if (n === 100) return 'cien';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const partes = [];
  if (c) partes.push(CENTENAS[c]);
  if (resto < 30) {
    if (resto) partes.push(UNIDADES[resto]);
  } else {
    const d = Math.floor(resto / 10);
    const u = resto % 10;
    partes.push(u ? `${DECENAS[d]} y ${UNIDADES[u]}` : DECENAS[d]);
  }
  return partes.join(' ');
}

// Delante de «mil» o de «millones», el uno se acorta. Y el acento SOLO lo
// lleva veintiún: se escribe «treinta y un mil», no «treinta y ún mil».
const apocopar = (t) => t.replace(/veintiuno$/, 'veintiún').replace(/uno$/, 'un');

// El monto en letras, como se escribe en un recibo de caja.
//
// «Un mil» no se dice: es «mil». Y cuando la cifra es de millones redondos
// lleva «de»: un millón DE pesos, pero un millón cien mil pesos.
export function numeroALetras(n) {
  const entero = Math.round(Math.abs(Number(n) || 0));
  if (entero === 0) return 'cero pesos';

  const millones = Math.floor(entero / 1000000);
  const miles = Math.floor((entero % 1000000) / 1000);
  const resto = entero % 1000;
  const partes = [];

  if (millones === 1) partes.push('un millón');
  else if (millones > 1) partes.push(`${apocopar(hasta999(millones))} millones`);

  if (miles === 1) partes.push('mil');
  else if (miles > 1) partes.push(`${apocopar(hasta999(miles))} mil`);

  if (resto) partes.push(hasta999(resto));

  const letras = partes.join(' ');
  const redondo = entero >= 1000000 && entero % 1000000 === 0;
  return `${letras} ${redondo ? 'de pesos' : 'pesos'}`;
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
               'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// Las fechas del recibo se leen en universal, igual que en el resto del
// programa: la fecha de un abono se guarda como medianoche de aquí y leerla en
// hora local la corre un día entero.
export function fechaDeRecibo(f) {
  const d = f instanceof Date ? f : new Date(String(f).length <= 10 ? f + 'T00:00:00Z' : f);
  if (isNaN(d.getTime())) return '';
  return `${d.getUTCDate()} de ${MESES[d.getUTCMonth()]} de ${d.getUTCFullYear()}`;
}

const plata = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// Cuánto quedaba debiendo JUSTO DESPUÉS de este abono, no hoy. Un recibo es la
// foto de un momento: si dijera el saldo de hoy, dos recibos del mismo cliente
// se contradirían entre ellos.
export function saldoDespuesDe(prestamo, pagos, pago) {
  const orden = (p) => [new Date(p.payment_date).getTime(), p.id || 0];
  const [fEste, idEste] = orden(pago);
  const previos = (pagos || []).filter(p => {
    const [f, id] = orden(p);
    return f < fEste || (f === fEste && id <= idEste);
  });
  const estado = estadoDelPrestamo(prestamo, previos, new Date(pago.payment_date));
  return estado.deudaTotal;
}

const escapar = (t) => String(t == null ? '' : t)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// El recibo NO lleva el nombre de quien presta, a propósito. Es un papel que
// se va con el cliente y anda por ahí: el nombre del prestamista impreso es una
// prueba en manos de cualquiera. El cliente ya sabe a quién le paga, y el
// número del recibo basta para encontrarlo en el programa.
export function htmlDelRecibo({ cliente, prestamo, pago, pagos }) {
  const saldo = saldoDespuesDe(prestamo, pagos, pago);
  const numero = pago.id ? String(pago.id).padStart(5, '0') : '—';
  const concepto = `Abono al préstamo de ${plata(prestamo.amount)} ` +
                   `del ${fechaDeRecibo(prestamo.start_date)}`;

  // Sale dos veces en la misma hoja: el original para el cliente y la copia
  // para quien cobra. Es como se hace con los talonarios de siempre, y ahorra
  // imprimir dos hojas.
  const cuerpo = (rotulo) => `
    <section class="recibo">
      <header>
        <div>
          <div class="empresa">RECIBO DE CAJA</div>
          <div class="tipo copia">${rotulo}</div>
        </div>
        <div class="numero">
          <div class="etiqueta">N.º</div>
          <div class="valor">${escapar(numero)}</div>
        </div>
      </header>

      <div class="fecha">${escapar(fechaDeRecibo(pago.payment_date))}</div>

      <dl>
        <dt>Recibí de</dt>
        <dd class="fuerte">${escapar(cliente.name)}${
          cliente.cedula ? `<span class="cedula">C.C. ${escapar(cliente.cedula)}</span>` : ''}</dd>

        <dt>La suma de</dt>
        <dd class="monto">${plata(pago.amount)}</dd>

        <dt>Son</dt>
        <dd class="letras">${escapar(numeroALetras(pago.amount))} M/CTE</dd>

        <dt>Por concepto de</dt>
        <dd>${escapar(concepto)}</dd>

        <dt>Saldo después de este abono</dt>
        <dd class="fuerte">${plata(saldo)}</dd>
      </dl>

      <div class="firmas">
        <div><span></span>Firma de quien entrega — ${escapar(cliente.name)}</div>
        <div><span></span>Firma de quien recibe</div>
      </div>
    </section>`;

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Recibo ${escapar(numero)} — ${escapar(cliente.name)}</title>
<style>
  @page { size: letter; margin: 12mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #111827; margin: 0; padding: 10mm; background: #f3f4f6;
  }
  .recibo {
    background: white; border: 1px solid #9ca3af; border-radius: 4px;
    padding: 7mm 8mm; margin-bottom: 6mm; max-width: 185mm;
  }
  header { display: flex; justify-content: space-between; align-items: flex-start;
           border-bottom: 2px solid #111827; padding-bottom: 3mm; }
  .empresa { font-size: 15pt; font-weight: 700; }
  .tipo { font-size: 8pt; letter-spacing: .14em; color: #4b5563; margin-top: 1mm; }
  .copia { color: #9ca3af; }
  .numero { text-align: right; }
  .numero .etiqueta { font-size: 7pt; color: #6b7280; letter-spacing: .1em; }
  .numero .valor { font-size: 14pt; font-weight: 700; font-variant-numeric: tabular-nums; }
  .fecha { text-align: right; font-size: 9pt; color: #4b5563; margin: 2mm 0 4mm; }
  dl { margin: 0; display: grid; grid-template-columns: 42mm 1fr; gap: 2.5mm 4mm; }
  dt { font-size: 8pt; color: #6b7280; text-transform: uppercase;
       letter-spacing: .06em; padding-top: 1mm; }
  dd { margin: 0; font-size: 11pt; }
  .fuerte { font-weight: 700; }
  .cedula { display: block; font-size: 9pt; font-weight: 400; color: #4b5563; }
  .monto { font-size: 19pt; font-weight: 700; font-variant-numeric: tabular-nums; }
  .letras { font-size: 10pt; text-transform: uppercase; letter-spacing: .02em; }
  .firmas { display: flex; gap: 12mm; margin-top: 14mm; font-size: 8pt; color: #4b5563; }
  .firmas div { flex: 1; }
  .firmas span { display: block; border-top: 1px solid #6b7280; margin-bottom: 1.5mm; }
  @media print {
    body { background: white; padding: 0; }
    .recibo { border: none; padding: 0 0 8mm; }
    .recibo + .recibo { border-top: 1px dashed #9ca3af; padding-top: 8mm; }
  }
</style>
</head>
<body onload="window.print()" onafterprint="window.close()">
${cuerpo('ORIGINAL — CLIENTE')}
${cuerpo('COPIA — ARCHIVO')}
</body>
</html>`;
}

// Abre el recibo en una ventana aparte y lo manda a imprimir. Desde el diálogo
// del navegador se elige la impresora o «Guardar como PDF».
export function imprimirRecibo(datos) {
  const ventana = window.open('', '_blank');
  if (!ventana) {
    alert('El navegador bloqueó la ventana del recibo.\n\n' +
          'Permite las ventanas emergentes para este sitio y vuelve a intentarlo.');
    return false;
  }
  ventana.document.open();
  ventana.document.write(htmlDelRecibo(datos));
  ventana.document.close();
  return true;
}
